import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon, TruckIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
const OrderBookVsDeliveriesDashboard: React.FC = () => {
  const kpis = [{
    title: 'Total Order Book',
    value: '₹3,450 Cr',
    change: '+12.5%',
    isPositive: true,
    subtitle: 'Current outstanding'
  }, {
    title: 'Delivery OTIF %',
    value: '87.3%',
    change: '+3.2%',
    isPositive: true,
    subtitle: 'On-Time In-Full'
  }, {
    title: 'Backlog %',
    value: '15.8%',
    change: '-2.1%',
    isPositive: true,
    subtitle: 'Of total order book'
  }];
  const orderVsShipmentData = [{
    month: 'Jan',
    orderBook: 2850,
    shipment: 2380,
    otif: 85.2
  }, {
    month: 'Feb',
    orderBook: 2920,
    shipment: 2620,
    otif: 86.1
  }, {
    month: 'Mar',
    orderBook: 3050,
    shipment: 2650,
    otif: 84.8
  }, {
    month: 'Apr',
    orderBook: 3120,
    shipment: 2900,
    otif: 87.5
  }, {
    month: 'May',
    orderBook: 3280,
    shipment: 2850,
    otif: 86.9
  }, {
    month: 'Jun',
    orderBook: 3350,
    shipment: 3020,
    otif: 88.2
  }, {
    month: 'Jul',
    orderBook: 3420,
    shipment: 3100,
    otif: 87.8
  }, {
    month: 'Aug',
    orderBook: 3480,
    shipment: 3180,
    otif: 88.5
  }, {
    month: 'Sep',
    orderBook: 3450,
    shipment: 3220,
    otif: 87.3
  }];
  const oemPendingOrdersData = [{
    oem: 'Maruti Suzuki',
    pending: 485,
    total: 1850,
    percentage: 26.2
  }, {
    oem: 'Hyundai',
    pending: 398,
    total: 1520,
    percentage: 26.2
  }, {
    oem: 'Tata Motors',
    pending: 425,
    total: 1380,
    percentage: 30.8
  }, {
    oem: 'Mahindra',
    pending: 285,
    total: 1050,
    percentage: 27.1
  }, {
    oem: 'Honda',
    pending: 195,
    total: 920,
    percentage: 21.2
  }, {
    oem: 'Toyota',
    pending: 158,
    total: 780,
    percentage: 20.3
  }, {
    oem: 'Kia',
    pending: 178,
    total: 710,
    percentage: 25.1
  }, {
    oem: 'VW Group',
    pending: 165,
    total: 620,
    percentage: 26.6
  }];
  const deliveryVarianceData = [{
    oem: 'Maruti Suzuki',
    north: 2.5,
    west: -1.2,
    south: 3.8,
    east: 5.2
  }, {
    oem: 'Hyundai',
    north: -0.8,
    west: 1.5,
    south: 2.2,
    east: 4.5
  }, {
    oem: 'Tata Motors',
    north: 4.2,
    west: 3.5,
    south: 6.8,
    east: 8.2
  }, {
    oem: 'Mahindra',
    north: 1.8,
    west: 0.5,
    south: 3.2,
    east: 5.8
  }, {
    oem: 'Honda',
    north: -1.5,
    west: -0.8,
    south: 1.2,
    east: 2.8
  }, {
    oem: 'Toyota',
    north: -2.2,
    west: -1.5,
    south: 0.5,
    east: 1.8
  }];
  const delayedDispatchData = [{
    orderNo: 'ORD-2024-1285',
    oem: 'Tata Motors',
    product: 'LED Headlamp Assembly',
    quantity: '2,500 units',
    scheduledDate: '15-Oct-2024',
    delayDays: 8,
    reason: 'Material shortage - semiconductor chips',
    impact: '₹18.5 Cr',
    status: 'Critical'
  }, {
    orderNo: 'ORD-2024-1312',
    oem: 'Mahindra',
    product: 'Bumper Assembly',
    quantity: '1,800 units',
    scheduledDate: '18-Oct-2024',
    delayDays: 5,
    reason: 'Production line downtime',
    impact: '₹12.8 Cr',
    status: 'High'
  }, {
    orderNo: 'ORD-2024-1298',
    oem: 'Hyundai',
    product: 'Wiring Harness',
    quantity: '3,200 units',
    scheduledDate: '20-Oct-2024',
    delayDays: 3,
    reason: 'Quality hold - testing required',
    impact: '₹22.4 Cr',
    status: 'Medium'
  }, {
    orderNo: 'ORD-2024-1345',
    oem: 'Maruti Suzuki',
    product: 'Door Handle Set',
    quantity: '4,500 units',
    scheduledDate: '22-Oct-2024',
    delayDays: 6,
    reason: 'Logistics delay - port congestion',
    impact: '₹15.2 Cr',
    status: 'High'
  }, {
    orderNo: 'ORD-2024-1367',
    oem: 'Kia',
    product: 'Tail Lamp Complete',
    quantity: '2,100 units',
    scheduledDate: '25-Oct-2024',
    delayDays: 2,
    reason: 'Vendor delay - raw material',
    impact: '₹8.5 Cr',
    status: 'Medium'
  }, {
    orderNo: 'ORD-2024-1389',
    oem: 'Honda',
    product: 'ECU Module',
    quantity: '1,500 units',
    scheduledDate: '28-Oct-2024',
    delayDays: 4,
    reason: 'Capacity constraint',
    impact: '₹19.8 Cr',
    status: 'High'
  }];
  const getVarianceColor = (value: number) => {
    const absValue = Math.abs(value);
    if (absValue < 2) return 'bg-green-500';
    if (absValue < 4) return 'bg-yellow-500';
    if (absValue < 6) return 'bg-orange-500';
    return 'bg-red-500';
  };
  const getVarianceIntensity = (value: number) => {
    const absValue = Math.abs(value);
    if (absValue < 2) return 0.3;
    if (absValue < 4) return 0.5;
    if (absValue < 6) return 0.7;
    return 1;
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Critical':
        return 'text-red-600 bg-red-50 border-red-300';
      case 'High':
        return 'text-orange-600 bg-orange-50 border-orange-300';
      case 'Medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-300';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-300';
    }
  };
  return <div data-id="element-8713">
      <DescriptionPanel title="Order Book vs Deliveries Performance" description="Comprehensive tracking of order fulfillment, delivery performance, and backlog management. Monitor OTIF metrics, identify bottlenecks, and track delayed dispatches to optimize supply chain execution." insights={['Order book at ₹3,450 Cr with 87.3% OTIF delivery performance', 'Tata Motors showing highest delivery variance at 6.8% in South region', '6 critical delayed orders worth ₹97.2 Cr - immediate action required', 'Backlog reduced to 15.8% from 17.9% last month - improving trend']} data-id="element-8714" />
      {/* KPI Tiles */}
      <div className="mb-8" data-id="element-8715">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-8716">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-8717">
              <div className="flex flex-col h-full" data-id="element-8718">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-8719">
                  {kpi.title}
                </div>
                <div className="text-xs text-gray-400 mb-3" data-id="element-8720">{kpi.subtitle}</div>
                <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-8721">
                  {kpi.value}
                </div>
                <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-8722">
                  {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-8723" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-8724" />}
                  {kpi.change} vs last month
                </div>
              </div>
            </Card>)}
        </div>
      </div>
      {/* Order Book vs Shipment Trend */}
      <Card className="p-6 mb-8" data-id="element-8725">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-8726">
          Order Book vs Shipment Trend
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-8727">
          Monthly comparison with OTIF performance (₹ Cr)
        </p>
        <ResponsiveContainer width="100%" height={350} data-id="element-8728">
          <ComposedChart data={orderVsShipmentData} data-id="element-8729">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-8730" />
            <XAxis dataKey="month" tick={{
            fontSize: 12
          }} data-id="element-8731" />
            <YAxis yAxisId="left" tick={{
            fontSize: 12
          }} data-id="element-8732" />
            <YAxis yAxisId="right" orientation="right" tick={{
            fontSize: 12
          }} domain={[80, 95]} data-id="element-8733" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} formatter={(value: any, name: string) => {
            if (name === 'OTIF %') return `${value}%`;
            return `₹${value} Cr`;
          }} data-id="element-8734" />
            <Legend data-id="element-8735" />
            <Bar yAxisId="left" dataKey="orderBook" fill="#3b82f6" name="Order Book" data-id="element-8736" />
            <Bar yAxisId="left" dataKey="shipment" fill="#10b981" name="Shipment" data-id="element-8737" />
            <Line yAxisId="right" type="monotone" dataKey="otif" stroke="#f59e0b" strokeWidth={3} name="OTIF %" dot={{
            r: 5,
            strokeWidth: 2
          }} data-id="element-8738" />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>
      {/* OEM-wise Pending Orders and Delivery Variance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-8739">
        <Card className="p-6" data-id="element-8740">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-8741">
            OEM-wise Pending Orders
          </h3>
          <ResponsiveContainer width="100%" height={350} data-id="element-8742">
            <BarChart data={oemPendingOrdersData} layout="vertical" data-id="element-8743">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-8744" />
              <XAxis type="number" tick={{
              fontSize: 11
            }} data-id="element-8745" />
              <YAxis dataKey="oem" type="category" width={120} tick={{
              fontSize: 11
            }} data-id="element-8746" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number, name: string) => {
              if (name === 'Pending' || name === 'Total') return `₹${value} Cr`;
              return `${value}%`;
            }} data-id="element-8747" />
              <Legend data-id="element-8748" />
              <Bar dataKey="pending" fill="#ef4444" name="Pending" data-id="element-8749" />
              <Bar dataKey="total" fill="#3b82f6" name="Total" opacity={0.3} data-id="element-8750" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-8751">
            <div className="text-sm text-gray-600 text-center" data-id="element-8752">
              Total Pending: ₹2,289 Cr across 8 OEMs
            </div>
          </div>
        </Card>
        <Card className="p-6" data-id="element-8753">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-8754">
            Delivery Variance Heat Map
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-8755">
            Delay days by OEM and Region (positive = delayed)
          </p>
          <div className="overflow-x-auto" data-id="element-8756">
            <table className="w-full" data-id="element-8757">
              <thead data-id="element-8758">
                <tr className="border-b border-gray-300" data-id="element-8759">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-700" data-id="element-8760">
                    OEM
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-700" data-id="element-8761">
                    North
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-700" data-id="element-8762">
                    West
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-700" data-id="element-8763">
                    South
                  </th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-gray-700" data-id="element-8764">
                    East
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-8765">
                {deliveryVarianceData.map((row, index) => <tr key={index} className="border-b border-gray-100" data-id="element-8766">
                    <td className="py-2 px-3 text-sm font-medium text-gray-800" data-id="element-8767">
                      {row.oem}
                    </td>
                    {['north', 'west', 'south', 'east'].map(region => {
                  const value = row[region as keyof typeof row] as number;
                  return <td key={region} className="py-2 px-3 text-center" data-id="element-8768">
                          <div className={`${getVarianceColor(value)} text-white font-semibold text-xs py-1.5 px-2 rounded`} style={{
                      opacity: getVarianceIntensity(value)
                    }} data-id="element-8769">
                            {value > 0 ? '+' : ''}
                            {value}d
                          </div>
                        </td>;
                })}
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-end space-x-2 text-xs" data-id="element-8770">
            <span className="text-gray-600" data-id="element-8771">Delay:</span>
            <div className="flex items-center space-x-1" data-id="element-8772">
              <div className="w-4 h-4 bg-green-500 rounded" data-id="element-8773" />
              <span className="text-gray-600" data-id="element-8774">&lt;2d</span>
            </div>
            <div className="flex items-center space-x-1" data-id="element-8775">
              <div className="w-4 h-4 bg-yellow-500 rounded" data-id="element-8776" />
              <span className="text-gray-600" data-id="element-8777">2-4d</span>
            </div>
            <div className="flex items-center space-x-1" data-id="element-8778">
              <div className="w-4 h-4 bg-orange-500 rounded" data-id="element-8779" />
              <span className="text-gray-600" data-id="element-8780">4-6d</span>
            </div>
            <div className="flex items-center space-x-1" data-id="element-8781">
              <div className="w-4 h-4 bg-red-500 rounded" data-id="element-8782" />
              <span className="text-gray-600" data-id="element-8783">&gt;6d</span>
            </div>
          </div>
        </Card>
      </div>
      {/* Delayed Dispatch Report Table */}
      <Card className="p-6" data-id="element-8784">
        <div className="flex items-center justify-between mb-4" data-id="element-8785">
          <div data-id="element-8786">
            <h3 className="text-lg font-semibold text-gray-800" data-id="element-8787">
              Delayed Dispatch Report
            </h3>
            <p className="text-sm text-gray-600" data-id="element-8788">
              Critical orders requiring immediate attention
            </p>
          </div>
          <div className="flex items-center space-x-2" data-id="element-8789">
            <AlertTriangleIcon size={20} className="text-red-600" data-id="element-8790" />
            <span className="text-sm font-semibold text-red-600" data-id="element-8791">
              6 Delayed Orders
            </span>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-8792">
          <table className="w-full" data-id="element-8793">
            <thead data-id="element-8794">
              <tr className="border-b-2 border-gray-300" data-id="element-8795">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8796">
                  Order No
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8797">
                  OEM
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8798">
                  Product
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8799">
                  Quantity
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8800">
                  Scheduled
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8801">
                  Delay
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8802">
                  Reason
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8803">
                  Impact
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-8804">
                  Status
                </th>
              </tr>
            </thead>
            <tbody data-id="element-8805">
              {delayedDispatchData.map((item, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-8806">
                  <td className="py-3 px-4 text-sm font-medium text-blue-600" data-id="element-8807">
                    {item.orderNo}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-800" data-id="element-8808">
                    {item.oem}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-800" data-id="element-8809">
                    {item.product}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-8810">
                    {item.quantity}
                  </td>
                  <td className="py-3 px-4 text-sm text-center text-gray-700" data-id="element-8811">
                    {item.scheduledDate}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-8812">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700" data-id="element-8813">
                      <TruckIcon size={12} className="mr-1" data-id="element-8814" />
                      {item.delayDays}d
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-8815">
                    {item.reason}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900" data-id="element-8816">
                    {item.impact}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-8817">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(item.status)}`} data-id="element-8818">
                      {item.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-8819">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-8820">
            <div className="text-center" data-id="element-8821">
              <div className="text-gray-600" data-id="element-8822">Total Delayed Orders</div>
              <div className="text-xl font-bold text-red-600" data-id="element-8823">6</div>
            </div>
            <div className="text-center" data-id="element-8824">
              <div className="text-gray-600" data-id="element-8825">Total Impact</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-8826">₹97.2 Cr</div>
            </div>
            <div className="text-center" data-id="element-8827">
              <div className="text-gray-600" data-id="element-8828">Avg Delay Days</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-8829">4.7</div>
            </div>
            <div className="text-center" data-id="element-8830">
              <div className="text-gray-600" data-id="element-8831">Critical Orders</div>
              <div className="text-xl font-bold text-red-600" data-id="element-8832">1</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default OrderBookVsDeliveriesDashboard;